import { axiosClient } from '../../../library/Service/axiosClient';
import MentorPortalService, { MentorMenteeCard } from './MentorPortalService';

/**
 * UAS-2376 pair-scoped messages (DEC-P14-019…026) — commonapi mentorship_pair_message.
 * SIMPLE THREAD + SEND only. No MESSAGE_SERVER. No unread/read state.
 */
const MENTORSHIP_BASE = `${process.env.REACT_APP_COMMON_API}/mentorship`;

export type MentorMessageBubble = {
    id: string | number | null;
    body: string;
    sentDate: string | null;
    isFromCurrentUser: boolean;
};

export type MentorConversationRow = {
    mentee: MentorMenteeCard;
    conversationId: string | null;
    lastMessagePreview: string | null;
    lastSentDate: string | null;
    unreadLabel: string;
};

export type MentorThreadPayload = {
    mentee: MentorMenteeCard;
    conversationId: string | null;
    messages: MentorMessageBubble[];
};

type MentorPairMessageRow = {
    id?: number | null;
    pairId?: number | null;
    senderRole?: string | null;
    senderId?: string | null;
    body?: string | null;
    createdAt?: string | null;
};

type MentorPairMessagesPayload = {
    messages?: MentorPairMessageRow[];
    pagination?: {
        page?: number;
        pageSize?: number;
        total?: number;
        totalPages?: number;
    };
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return null;
};

const unwrap = <T>(response: { data?: { data?: T } | T }): T => {
    const payload = response?.data;
    if (payload && typeof payload === 'object' && 'data' in payload) {
        return (payload as { data: T }).data;
    }
    return payload as T;
};

const currentUserUuid = (): string | null => {
    const fromSession = String(sessionStorage.getItem('user-id') || '').trim();
    if (fromSession) return fromSession;
    const fromLocal = String(localStorage.getItem('user-id') || '').trim();
    if (fromLocal) return fromLocal;
    try {
        const raw = sessionStorage.getItem('user') || localStorage.getItem('user');
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { id?: unknown };
        const id = parsed?.id == null ? '' : String(parsed.id).trim();
        return id || null;
    } catch {
        return null;
    }
};

const mapBubble = (row: MentorPairMessageRow, me: string | null): MentorMessageBubble => ({
    id: row.id ?? null,
    body: String(row.body || ''),
    sentDate: row.createdAt ? String(row.createdAt) : null,
    isFromCurrentUser: me ? String(row.senderId || '') === me : row.senderRole === 'MENTOR',
});

const fetchPairMessagesPage = async (
    pairId: number,
    page = 1
): Promise<MentorPairMessagesPayload> => {
    const response = await axiosClient().get(
        `${MENTORSHIP_BASE}/mentor/pairs/${pairId}/messages`,
        { params: { page } }
    );
    return unwrap<MentorPairMessagesPayload>(response);
};

const latestMessageForPair = async (pairId: number): Promise<MentorPairMessageRow | null> => {
    const first = await fetchPairMessagesPage(pairId, 1);
    const total = first.pagination?.total ?? 0;
    if (!total) return null;
    const pageSize = first.pagination?.pageSize ?? 25;
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    const pageData = lastPage === 1 ? first : await fetchPairMessagesPage(pairId, lastPage);
    const messages = pageData.messages || [];
    return messages.length > 0 ? messages[messages.length - 1] : null;
};

const fetchThreadMessages = async (pairId: number, me: string | null): Promise<MentorMessageBubble[]> => {
    const first = await fetchPairMessagesPage(pairId, 1);
    const total = first.pagination?.total ?? 0;
    const pageSize = first.pagination?.pageSize ?? 25;
    const totalPages = first.pagination?.totalPages ?? (pageSize > 0 ? Math.ceil(total / pageSize) : 0);
    const rows: MentorPairMessageRow[] = [...(first.messages || [])];
    if (totalPages > 1) {
        for (let page = 2; page <= totalPages; page += 1) {
            const next = await fetchPairMessagesPage(pairId, page);
            rows.push(...(next.messages || []));
        }
    }
    return rows.map((row) => mapBubble(row, me));
};

class MentorMessagesService {
    static currentUserUuid(): string | null {
        return currentUserUuid();
    }

    static isAuthFailure(error: { status?: number } | unknown): boolean {
        const status = (error as { status?: number })?.status;
        return status === 401 || status === 403;
    }

    static errorMessage(error: unknown): string {
        return MentorPortalService.errorMessage(error);
    }

    static async requirePublishedMentee(menteeUuid: string): Promise<MentorMenteeCard> {
        const menteesPayload = await MentorPortalService.getMentees();
        const mentee = (menteesPayload.mentees || []).find((row) => row.menteeUuid === menteeUuid);
        if (!mentee) {
            throw {
                isError: true,
                message: 'Mentee is not in your Published pairs.',
                status: 404,
            };
        }
        return mentee;
    }

    /**
     * One preview fetch per Published pair: GET .../messages, last row of final page.
     */
    static async listConversations(): Promise<MentorConversationRow[]> {
        const menteesPayload = await MentorPortalService.getMentees();
        const mentees = menteesPayload.mentees || [];
        const previews = await Promise.all(
            mentees.map(async (mentee) => {
                const pairId = mentee.pairId;
                if (pairId == null) {
                    return { mentee, latest: null as MentorPairMessageRow | null };
                }
                try {
                    const latest = await latestMessageForPair(pairId);
                    return { mentee, latest };
                } catch {
                    return { mentee, latest: null as MentorPairMessageRow | null };
                }
            })
        );
        return previews.map(({ mentee, latest }) => ({
            mentee,
            conversationId: mentee.pairId != null ? String(mentee.pairId) : null,
            lastMessagePreview: latest?.body ? String(latest.body) : null,
            lastSentDate: latest?.createdAt ? String(latest.createdAt) : null,
            unreadLabel: '—',
        }));
    }

    static async getThread(menteeUuid: string): Promise<MentorThreadPayload> {
        const mentee = await MentorMessagesService.requirePublishedMentee(menteeUuid);
        const pairId = mentee.pairId;
        if (pairId == null) {
            return { mentee, conversationId: null, messages: [] };
        }
        const me = currentUserUuid();
        const messages = await fetchThreadMessages(pairId, me);
        return {
            mentee,
            conversationId: String(pairId),
            messages,
        };
    }

    static async send(menteeUuid: string, body: string): Promise<MentorThreadPayload> {
        const mentee = await MentorMessagesService.requirePublishedMentee(menteeUuid);
        const text = String(body || '').trim();
        if (!text) {
            throw {
                isError: true,
                message: 'body is required',
                status: 400,
            };
        }
        const pairId = mentee.pairId;
        if (pairId == null) {
            throw {
                isError: true,
                message: 'Mentee is not in your Published pairs.',
                status: 404,
            };
        }
        await axiosClient().post(`${MENTORSHIP_BASE}/mentor/pairs/${pairId}/messages`, {
            body: text,
        });
        return MentorMessagesService.getThread(menteeUuid);
    }
}

export default MentorMessagesService;
